import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Download, ExternalLink, Loader2, X } from 'lucide-react';
import AccountLayout from '../components/AccountLayout';

export function DocumentViewerPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const documentUrl = searchParams.get('url') || '';
  const documentName = searchParams.get('name') || 'document';

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [isPdf, setIsPdf] = useState(false);

  const safeFileName = useMemo(() => documentName.replace(/[^\w\s.-]+/g, '_'), [documentName]);

  useEffect(() => {
    let revokedUrl: string | null = null;

    const loadDocument = async () => {
      if (!documentUrl) {
        setError('Lien du document manquant');
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);
      try {
        const token = localStorage.getItem('client_token');
        const response = await fetch(documentUrl, {
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        });

        if (!response.ok) {
          throw new Error('Impossible de charger le document');
        }

        const contentType = response.headers.get('content-type') || '';
        setIsPdf(contentType.includes('application/pdf'));

        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        revokedUrl = url;
        setBlobUrl(url);
      } catch (e: any) {
        setError(e?.message || 'Impossible de charger le document');
      } finally {
        setLoading(false);
      }
    };

    void loadDocument();

    return () => {
      if (revokedUrl) URL.revokeObjectURL(revokedUrl);
    };
  }, [documentUrl]);

  const handleBack = () => navigate(-1);

  const handleClose = () => {
    window.close();
    window.setTimeout(() => navigate(-1), 150);
  };

  const handleOpenNewTab = () => {
    if (!blobUrl) return;
    window.open(blobUrl, '_blank', 'noopener,noreferrer');
  };

  const handleDownload = () => {
    if (!blobUrl) return;
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = safeFileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  return (
    <AccountLayout title="Aperçu document" subtitle={safeFileName}>
      <div className="w-full px-4 sm:px-6 lg:px-8 py-6">
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <button
                onClick={handleBack}
                className="px-3 py-2 text-sm rounded-md border border-gray-300 hover:bg-gray-50 inline-flex items-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Retour
              </button>
              <span className="text-xs text-textSecondary truncate max-w-[18rem]">{safeFileName}</span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleOpenNewTab}
                disabled={!blobUrl}
                className="px-3 py-2 text-sm rounded-md border border-gray-300 hover:bg-gray-50 disabled:opacity-50 inline-flex items-center gap-2"
              >
                <ExternalLink className="w-4 h-4" />
                Nouvel onglet
              </button>
              <button
                onClick={handleDownload}
                disabled={!blobUrl}
                className="px-3 py-2 text-sm rounded-md border border-gray-300 hover:bg-gray-50 disabled:opacity-50 inline-flex items-center gap-2"
              >
                <Download className="w-4 h-4" />
                Télécharger
              </button>
              <button
                onClick={handleClose}
                className="px-3 py-2 text-sm rounded-md bg-gray-800 text-white hover:bg-gray-900 inline-flex items-center gap-2"
              >
                <X className="w-4 h-4" />
                Fermer
              </button>
            </div>
          </div>

          <div className="h-[70vh] p-4 bg-gray-100">
            {loading && (
              <div className="h-full flex items-center justify-center text-textSecondary gap-2">
                <Loader2 className="w-5 h-5 animate-spin" />
                Chargement du document...
              </div>
            )}
            {!loading && error && (
              <div className="h-full flex items-center justify-center text-red-600">{error}</div>
            )}
            {!loading && !error && blobUrl && isPdf && (
              <iframe src={blobUrl} title="Document preview" className="w-full h-full rounded-lg border border-gray-300 bg-white" />
            )}
            {!loading && !error && blobUrl && !isPdf && (
              <div className="h-full flex items-center justify-center">
                <div className="bg-white border border-gray-200 rounded-lg p-6 text-center max-w-md">
                  <p className="text-sm text-textSecondary mb-4">
                    L'aperçu direct n'est pas disponible pour ce format. Utilisez les actions ci-dessus.
                  </p>
                  <div className="flex items-center justify-center gap-2">
                    <button
                      onClick={handleOpenNewTab}
                      className="px-3 py-2 text-sm rounded-md border border-gray-300 hover:bg-gray-50 inline-flex items-center gap-2"
                    >
                      <ExternalLink className="w-4 h-4" />
                      Ouvrir
                    </button>
                    <button
                      onClick={handleDownload}
                      className="px-3 py-2 text-sm rounded-md border border-gray-300 hover:bg-gray-50 inline-flex items-center gap-2"
                    >
                      <Download className="w-4 h-4" />
                      Télécharger
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </AccountLayout>
  );
}

export default DocumentViewerPage;
